import { useState } from 'react'
import { Plus, ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { StaticScenarioForm } from './StaticScenarioForm'
import { SequenceScenarioForm } from './SequenceScenarioForm'
import { ConditionalScenarioForm } from './ConditionalScenarioForm'
import { StatefulCollectionForm } from './StatefulCollectionForm'

type ScenarioTypeChoice = 'static' | 'sequence' | 'conditional' | 'stateful_collection' | null

interface TypeOptionProps {
  title: string
  description: string
  onClick: () => void
}

function TypeOption({ title, description, onClick }: TypeOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded-md border p-4 hover:bg-accent hover:text-accent-foreground transition-colors space-y-1"
    >
      <p className="font-medium text-sm">{title}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
    </button>
  )
}

interface ScenarioFormProps {
  projectId: string
  endpointId: string
}

export function AddScenarioDialog({ projectId, endpointId }: ScenarioFormProps) {
  const [open, setOpen] = useState(false)
  const [selectedType, setSelectedType] = useState<ScenarioTypeChoice>(null)
  const { t } = useTranslation()

  const handleClose = () => {
    setOpen(false)
    setSelectedType(null)
  }

  const handleOpenChange = (v: boolean) => {
    setOpen(v)
    if (!v) setSelectedType(null)
  }

  const dialogTitle =
    selectedType === 'static' ? t('scenarioForm.staticTitle') :
    selectedType === 'sequence' ? t('scenarioForm.sequenceTitle') :
    selectedType === 'conditional' ? t('scenarioForm.conditionalTitle') :
    selectedType === 'stateful_collection' ? t('scenarioForm.statefulTitle') :
    t('scenarioForm.selectType')

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          {t('scenarioForm.addScenario')}
        </Button>
      </DialogTrigger>
      <DialogContent className={cn(
        'max-h-[90vh] overflow-y-auto',
        selectedType === 'conditional' || selectedType === 'stateful_collection' ? 'max-w-2xl' : 'max-w-lg',
      )}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            {selectedType !== null && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => setSelectedType(null)}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <DialogTitle>{dialogTitle}</DialogTitle>
          </div>
        </DialogHeader>

        {selectedType === null && (
          <div className="space-y-2 pt-1">
            <TypeOption
              title="Static"
              description={t('scenarioForm.staticDesc')}
              onClick={() => setSelectedType('static')}
            />
            <TypeOption
              title="Sequence"
              description={t('scenarioForm.sequenceDesc')}
              onClick={() => setSelectedType('sequence')}
            />
            <TypeOption
              title="Conditional"
              description={t('scenarioForm.conditionalDesc')}
              onClick={() => setSelectedType('conditional')}
            />
            <TypeOption
              title="Stateful Collection"
              description={t('scenarioForm.statefulDesc')}
              onClick={() => setSelectedType('stateful_collection')}
            />
          </div>
        )}

        {selectedType === 'static' && (
          <StaticScenarioForm
            projectId={projectId}
            endpointId={endpointId}
            onSuccess={handleClose}
            onCancel={handleClose}
          />
        )}

        {selectedType === 'sequence' && (
          <SequenceScenarioForm
            projectId={projectId}
            endpointId={endpointId}
            onSuccess={handleClose}
            onCancel={handleClose}
          />
        )}

        {selectedType === 'conditional' && (
          <ConditionalScenarioForm
            projectId={projectId}
            endpointId={endpointId}
            onSuccess={handleClose}
            onCancel={handleClose}
          />
        )}

        {selectedType === 'stateful_collection' && (
          <StatefulCollectionForm
            projectId={projectId}
            endpointId={endpointId}
            onSuccess={handleClose}
            onCancel={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
